"use strict";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import _ from "underscore";
import PartialFetcher from "./partial_fetcher";
import DocumentGroupStore from "../stores/document_group_store";
import ObjectWithIDStore from "../stores/object_with_id_store";
import DataStoreService from "./data_store_service";
import fetch from "../util/fetch";
import Account from "../api/account";
class DocumentsService {
  constructor() {
    __publicField(this, "documents", {});
    __publicField(this, "documentBatches", new ObjectWithIDStore());
    __publicField(this, "documentsByLoadId", {});
    __publicField(this, "types", [
      { name: "Bill of Lading", value: "billOfLading", pos: 3 },
      { name: "Factoring Assignment", value: "factoringAssignment", pos: 0 },
      { name: "Invoice", value: "invoice", pos: 1 },
      { name: "Other", value: "other", pos: 7 },
      { name: "Packing Slip", value: "packingSlip", pos: 6 },
      { name: "Delivery Receipt", value: "proofOfDelivery", pos: 4 },
      { name: "Rate Confirmation", value: "loadTender", pos: 2 },
      { name: "Weight Ticket / Gate Pass", value: "weightTicket", pos: 5 }
    ]);
    __publicField(this, "typesMap", {});
    __publicField(this, "documentsFromIds", (ids) => {
      return _.compact(ids.map((id) => this.documents[id]));
    });
    __publicField(this, "fetchMissingDocuments", (ids, docs) => {
      const toFetch = _.difference(ids, _.pluck(docs, "id"));
      return fetch("/api/documents/multiple?ids=" + toFetch.join(",")).then(
        (data) => {
          data.documents.forEach((doc) => this.documents[doc.id] = doc);
          return this.documentsFromIds(ids);
        }
      );
    });
    __publicField(this, "fetchDocumentBatch", (id) => (
      // Only check duplicates for payments network accounts
      Account.paymentsNetworkEnabled() ? Promise.all([
        fetch("/api/imports/document_batches/" + id),
        fetch(
          "/api/document_batches/" + id + "/approved_carrier_invoice_duplicate"
        ).then(
          (dupe) => dupe.carrier_invoice && dupe.carrier_invoice.id ? dupe : {
            carrier_invoice: void 0,
            carrier_invoice_submission: void 0
          }
        ).catch(() => ({
          carrier_invoice: void 0,
          carrier_invoice_submission: void 0
        }))
      ]).then(
        ([
          batchData,
          {
            // @ts-expect-error
            carrier_invoice: duplicateInvoice,
            // @ts-expect-error
            carrier_invoice_submission: carrierInvoiceSubmission
          }
        ]) => {
          const batch = this.updateDocumentBatch(__spreadProps(__spreadValues({}, batchData.document_batch), {
            duplicateInvoice,
            carrierInvoiceSubmission
          }));
          DataStoreService.updateStores(_.omit(batchData, "document_batch"));
          return batch;
        }
      ) : fetch(
        "/api/imports/document_batches/" + id
      ).then((data) => {
        const batch = this.updateDocumentBatch(data.document_batch);
        DataStoreService.updateStores(_.omit(data, "document_batch"));
        return batch;
      })
    ));
    __publicField(this, "getDocumentById", (id) => {
      return this.documents[id];
    });
    __publicField(this, "getDocuments", (ids = []) => {
      const docs = this.documentsFromIds(ids);
      if (docs.length === ids.length) {
        return Promise.resolve(docs);
      }
      return this.fetchMissingDocuments(ids, docs);
    });
    __publicField(this, "documentBatchFetcher", new PartialFetcher(
      this.documentBatches,
      (ids) => {
        return fetch("/api/imports/document_batches/bulk", {
          data: { ids }
        }).then((data) => data.document_batches);
      }
    ));
    __publicField(this, "getUncachedDocumentBatch", (id) => {
      return this.fetchDocumentBatch(id);
    });
    __publicField(this, "updateDocumentBatch", (batch) => {
      if (batch.documents) {
        this.updateDocuments(batch.documents);
        batch.documents = _.compact(
          batch.documents.map((document) => {
            return this.documents[document.id];
          })
        );
      }
      batch.document_groups = DocumentGroupStore.addOrUpdateAll(
        batch.document_groups
      );
      return this.documentBatches.addOrUpdate(batch);
    });
    __publicField(this, "updateDocuments", (documents) => {
      documents.forEach((document) => {
        const existing = this.documents[document.id];
        if (existing) {
          _.extend(existing, document);
          if (document.deleted_at) {
            delete this.documents[document.id];
          }
        } else {
          this.documents[document.id] = document;
        }
      });
    });
    __publicField(this, "replaceDocumentPage", (page) => {
      const doc = this.documents[page.document_id];
      const findKey = page.parent_id || page.id;
      if (doc && findKey) {
        const pageToReplace = doc.pages.filter(
          (p) => p.parent_id === findKey || p.id === findKey
        )[0];
        return Object.assign(pageToReplace, page);
      }
    });
    this.types.forEach((type) => {
      this.typesMap[type.value] = type;
    });
  }
  getDocumentBatches(ids, validator) {
    return this.documentBatchFetcher.fetchItemsIfNeeded(ids, validator);
  }
  getDocumentBatch(id) {
    return this.documentBatchFetcher.fetchItemIfNeeded(id);
  }
  getFetchedDocumentBatch(id) {
    return this.documentBatches.find(id);
  }
}
const documentsService = new DocumentsService();
export default documentsService;
